import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import VehicleTypeSelector from '../components/Filters/TypeSelector/VehicleTypeSelector';
import PartnerDealersListSelector from '../components/Filters/PartnerDealersList/PartnerDealersListSelector';
import BodyStyle from '../components/Filters/BodyStyle/BodyStyleList';
import DrivetrainSelector from '../components/Filters/Drivetrain/DrivetrainSelector';
import ExteriorSelector from '../components/Filters/Exterior/ExteriorSelector';
import FuelTypeSelector from '../components/Filters/FuelType/FuelTypeSelector';
import GearboxSelector from '../components/Filters/Gearbox/GearboxSelector';
import InteriorSelector from '../components/Filters/Interior/Interior';
import MileageSelector from '../components/Filters/Mileage/MileageSelector';
import ModelFamilySelector from '../components/Filters/ModelFamily/ModelFamilySelector';
import ModelYearSelector from '../components/Filters/ModelYear/ModelYearSelector';
import PowerSelector from '../components/Filters/Power/PowerSelector';
import PriceSelector from '../components/Filters/Price/PriceSelector';
import TrimLineSelector from '../components/Filters/TrimLine/TrimLineSelector';
import WheelSizeSelector from '../components/Filters/WheelSize/WheelSizeSelector';
import { selectAppConfig } from '../redux/appConfigReducer';
import {
  ExposedFiltersTypes,
  NEW_VEHICLE_FILTERS_LIST,
  NEW_VEHICLE_FILTERS_LIST_TIER_3,
  USED_VEHICLE_FILTERS_LIST,
  USED_VEHICLE_FILTERS_LIST_TIER_3,
  USED_COMBINED_FILTERS_LIST_TIER_3,
} from '../constants/initialFilters';
import OtherMakes from '../components/Filters/OtherMakes';
import NonAudiModels from '../components/Filters/NonAudiModels';
import useFilterOptions from './Filters/useFilterOptions';
import { selectFilters } from '../redux/filtersReducer';
import { CommonModelFamiliesValues as COMMON_MODELS } from '../components/Filters/ModelFamily/modelFamiliesConfigs';
import { FilterOption } from '../types';

type HandleAddFilter = (filterType: string, filterValue: string) => void;
type HandleExposeFilter = (filterType: string) => void;
type HandleChangeRange = (filterType: string, filterValue: number[]) => void;

export type UsedInventoryTypeOptions = {
  [key: string]: FilterOption;
};

export function computeSelectedShowOptionsUsedInventory(
  vehicleTypeDisplayOptions: string[] | undefined,
  usedInventoryTypeOptions: UsedInventoryTypeOptions,
): UsedInventoryTypeOptions {
  if (vehicleTypeDisplayOptions && vehicleTypeDisplayOptions.length > 0) {
    const filtered = Object.fromEntries(
      Object.entries(usedInventoryTypeOptions).filter(([key]) =>
        vehicleTypeDisplayOptions.includes(key),
      ),
    ) as UsedInventoryTypeOptions;

    return Object.keys(filtered).length > 0 ? filtered : usedInventoryTypeOptions;
  }
  return usedInventoryTypeOptions;
}

const useFilterListBuilder = (
  handleAddFilter: HandleAddFilter,
  openedFiltersList: ExposedFiltersTypes,
  handleExposeFilter: HandleExposeFilter,
  handleChangeRange: HandleChangeRange,
  isVehiclesLoading: boolean,
) => {
  const { availableFilters, confirmedFilters } = useSelector(selectFilters);
  const {
    listOfDealers,
    isTier3page,
    filterGroupsToRender,
    commonModelFamilies,
    vehicleTypeDisplayOptions,
    filterPositions,
  } = useSelector(selectAppConfig);
  const {
    vehicleTypePosition,
    listOfDealersPosition,
    bodyTypePosition,
    makeTypesPosition,
    modelFamilyPosition,
    modelYearPosition,
    trimLinePosition,
    pricePosition,
    mileagePosition,
    exteriorColorPosition,
    interiorColorPosition,
    fuelTypePosition,
    driveTrainPosition,
    wheelSizePosition,
    gearboxPosition,
    powerPosition,
  } = filterPositions;

  const isMultipleDealers = listOfDealers.length > 1;

  const isUsedVehicleFilter = confirmedFilters.vehicleType === 'used';
  const isNonAudiFilterActive =
    confirmedFilters.nonAudi === 'foreign-brand.yes' ||
    confirmedFilters.usedInventoryType === 'has-warranties.nonAudi';
  const isCombinedFiltersActive = confirmedFilters.usedInventoryType === 'has-warranties.combined';
  const usedFiltersListTier3 =
    isCombinedFiltersActive || isNonAudiFilterActive
      ? USED_COMBINED_FILTERS_LIST_TIER_3
      : USED_VEHICLE_FILTERS_LIST_TIER_3;

  const {
    vehicleTypeOptions,
    newInventoryTypeOptions,
    usedInventoryTypeOptions,
    bodyTypeOptions,
    modelFamilyOptions,
    trimlineOptions,
    exteriorColorOptions,
    interiorColorOptions,
    fuelTypeOptions,
    gearBoxOptions,
    wheelSizeOptions,
    drivetrainOptions,
    modelYearOptions,
    powerOptions,
    mileageOptions,
    priceOptions,
    otherMakesOptions,
    nonAudiModelsOptions,
  } = useFilterOptions();

  // ...your existing component/hook that calls the helper inside useMemo
  const selectedShowOptionsUsedInventory = useMemo(() => {
    return computeSelectedShowOptionsUsedInventory(
      vehicleTypeDisplayOptions,
      usedInventoryTypeOptions,
    );
  }, [vehicleTypeDisplayOptions, usedInventoryTypeOptions]);

  const hasAnyOtherMakeCount = !!otherMakesOptions;

  const vehicleTypeFilter = useMemo(() => {
    return {
      value: 'vehicleType',
      position: vehicleTypePosition,
      Component: (
        <VehicleTypeSelector
          filterType="vehicleType"
          vehicleTypeOptions={vehicleTypeOptions}
          availableFilters={availableFilters}
          confirmedFilters={confirmedFilters}
          handleAddFilter={handleAddFilter}
          usedInventoryTypeOptions={selectedShowOptionsUsedInventory}
          newInventoryTypeOptions={newInventoryTypeOptions}
          isTier3page={isTier3page}
          // nonAudiOption={nonAudiOption}
          isVehiclesLoading={isVehiclesLoading}
        />
      ),
    };
  }, [
    vehicleTypePosition,
    vehicleTypeOptions,
    availableFilters,
    confirmedFilters,
    handleAddFilter,
    selectedShowOptionsUsedInventory,
    newInventoryTypeOptions,
    isTier3page,
    // nonAudiOption,
    isVehiclesLoading,
  ]);

  const partnerDealerFilter = useMemo(() => {
    return {
      value: 'partnerDealers',
      position: listOfDealersPosition,
      Component: isMultipleDealers && (
        <PartnerDealersListSelector
          filterType="partnerDealers"
          filterOptions={listOfDealers}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    listOfDealersPosition,
    handleAddFilter,
    handleExposeFilter,
    isMultipleDealers,
    listOfDealers,
    openedFiltersList,
    confirmedFilters,
  ]);

  const otherMakesFilter = useMemo(() => {
    return {
      value: 'otherMakes',
      position: makeTypesPosition,
      Component: (isNonAudiFilterActive || isCombinedFiltersActive) && hasAnyOtherMakeCount && (
        <OtherMakes
          filterType="otherMakes"
          otherMakesOptions={otherMakesOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    makeTypesPosition,
    isNonAudiFilterActive,
    hasAnyOtherMakeCount,
    isCombinedFiltersActive,
    otherMakesOptions,
    confirmedFilters,
    openedFiltersList,
    handleAddFilter,
    handleExposeFilter,
  ]);

  const nonAudiModelsFilter = useMemo(() => {
    return {
      value: 'nonAudiModels',
      Component: !!confirmedFilters?.otherMakes?.length &&
        (isNonAudiFilterActive || isCombinedFiltersActive) &&
        hasAnyOtherMakeCount && (
          <NonAudiModels
            filterType="nonAudiModels"
            nonAudiModelsOptions={nonAudiModelsOptions}
            confirmedFilters={confirmedFilters}
            openedFiltersList={openedFiltersList}
            handleAddFilter={handleAddFilter}
            handleExposeFilter={handleExposeFilter}
          />
        ),
    };
  }, [
    isNonAudiFilterActive,
    isCombinedFiltersActive,
    hasAnyOtherMakeCount,
    nonAudiModelsOptions,
    confirmedFilters,
    openedFiltersList,
    handleAddFilter,
    handleExposeFilter,
  ]);

  const bodyStyleFilter = useMemo(() => {
    return {
      value: 'bodyType',
      position: bodyTypePosition,
      Component: (
        <BodyStyle
          filterType="bodyType"
          bodyTypeOptions={bodyTypeOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    bodyTypeOptions,
    bodyTypePosition,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
    confirmedFilters,
  ]);

  const modelFamilyFilter = useMemo(() => {
    return {
      value: 'modelFamily',
      position: modelFamilyPosition,
      Component: !isNonAudiFilterActive && (
        <ModelFamilySelector
          filterType="modelFamily"
          isTier3page={isTier3page}
          modelFamilyOptions={modelFamilyOptions}
          commonModelFamilies={isTier3page ? (commonModelFamilies as string[]) : COMMON_MODELS}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    modelFamilyPosition,
    isNonAudiFilterActive,
    modelFamilyOptions,
    isTier3page,
    commonModelFamilies,
    confirmedFilters,
    openedFiltersList,
    handleAddFilter,
    handleExposeFilter,
  ]);

  const trimlineFilter = useMemo(() => {
    return {
      value: 'trimLine',
      position: trimLinePosition,
      Component: (
        <TrimLineSelector
          filterType="trimLine"
          trimlineOptions={trimlineOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    trimLinePosition,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
    confirmedFilters,
    trimlineOptions,
  ]);

  const exteriorColorFilter = useMemo(() => {
    return {
      value: 'exteriorColor',
      position: exteriorColorPosition,
      Component: (
        <ExteriorSelector
          filterType="exteriorColor"
          exteriorColorOptions={exteriorColorOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    exteriorColorPosition,
    exteriorColorOptions,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
    confirmedFilters,
  ]);

  const interiorColorFilter = useMemo(() => {
    return {
      value: 'interiorColor',
      position: interiorColorPosition,
      Component: (
        <InteriorSelector
          filterType="interiorColor"
          interiorColorOptions={interiorColorOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    interiorColorPosition,
    handleAddFilter,
    handleExposeFilter,
    interiorColorOptions,
    openedFiltersList,
    confirmedFilters,
  ]);

  const fuelTypeFilter = useMemo(() => {
    return {
      value: 'fuelType',
      position: fuelTypePosition,
      Component: (
        <FuelTypeSelector
          filterType="fuelType"
          filterOptions={fuelTypeOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    fuelTypePosition,
    fuelTypeOptions,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
    confirmedFilters,
  ]);

  const drivetrainFilter = useMemo(() => {
    return {
      value: 'drivetrain',
      position: driveTrainPosition,
      Component: (
        <DrivetrainSelector
          filterType="drivetrain"
          filterOptions={drivetrainOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    driveTrainPosition,
    drivetrainOptions,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
    confirmedFilters,
  ]);

  const wheelSizeFilter = useMemo(() => {
    return {
      value: 'wheelSize',
      position: wheelSizePosition,
      Component: (
        <WheelSizeSelector
          filterType="wheelSize"
          filterOptions={wheelSizeOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    wheelSizePosition,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
    confirmedFilters,
    wheelSizeOptions,
  ]);

  const gearboxFilter = useMemo(() => {
    return {
      value: 'gearBox',
      position: gearboxPosition,
      Component: (
        <GearboxSelector
          filterType="gearBox"
          filterOptions={gearBoxOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    gearboxPosition,
    gearBoxOptions,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
    confirmedFilters,
  ]);

  const modelYearFilter = useMemo(() => {
    return {
      value: 'modelYear',
      position: modelYearPosition,
      Component: (
        <ModelYearSelector
          filterType="modelYear"
          filterOptions={modelYearOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    modelYearPosition,
    modelYearOptions,
    confirmedFilters,
    openedFiltersList,
    handleAddFilter,
    handleExposeFilter,
  ]);

  const mileageFilter = useMemo(() => {
    return {
      value: 'mileage',
      position: mileagePosition,
      Component: (
        <MileageSelector
          filterType="mileage"
          filterOptions={mileageOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleChangeRange={handleChangeRange}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    mileagePosition,
    handleChangeRange,
    handleExposeFilter,
    mileageOptions,
    openedFiltersList,
    confirmedFilters,
  ]);

  const priceFilter = useMemo(() => {
    return {
      value: 'price',
      position: pricePosition,
      Component: (
        <PriceSelector
          filterType="price"
          filterOptions={priceOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleChangeRange={handleChangeRange}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    pricePosition,
    handleChangeRange,
    handleExposeFilter,
    openedFiltersList,
    priceOptions,
    confirmedFilters,
  ]);

  const powerFilter = useMemo(() => {
    return {
      value: 'power',
      position: powerPosition,
      Component: (
        <PowerSelector
          filterType="power"
          filterOptions={powerOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleChangeRange={handleChangeRange}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    powerPosition,
    handleChangeRange,
    handleExposeFilter,
    openedFiltersList,
    powerOptions,
    confirmedFilters,
  ]);
  // Left staticFilterOrder in place for now. Used for locking positions of certain filters for UX purposes
  //
  // const staticFilterOrder = useMemo(
  //   () => [vehicleTypeFilter, partnerDealerFilter],
  //   [vehicleTypeFilter, partnerDealerFilter],
  // );

  // eslint-disable-next-line
  const insertAfter = (arr: any[], target: any, newItem: any) => {
    const index = arr.indexOf(target); // Find the index of the target
    if (index !== -1) {
      arr.splice(index + 1, 0, newItem); // Insert after the target
    }
    return arr;
  };

  const configuredFilterOrder = useMemo(() => {
    const arr = [
      vehicleTypeFilter,
      partnerDealerFilter,
      otherMakesFilter,
      bodyStyleFilter,
      modelFamilyFilter,
      trimlineFilter,
      modelYearFilter,
      mileageFilter,
      exteriorColorFilter,
      interiorColorFilter,
      priceFilter,
      fuelTypeFilter,
      drivetrainFilter,
      wheelSizeFilter,
      gearboxFilter,
      powerFilter,
    ];
    arr.sort((a, b) => {
      return (a.position || 0) - (b.position || 0);
    });
    const updatedArr = insertAfter(arr, otherMakesFilter, nonAudiModelsFilter);
    return updatedArr;
  }, [
    vehicleTypeFilter,
    partnerDealerFilter,
    otherMakesFilter,
    nonAudiModelsFilter,
    bodyStyleFilter,
    modelFamilyFilter,
    trimlineFilter,
    modelYearFilter,
    mileageFilter,
    exteriorColorFilter,
    interiorColorFilter,
    priceFilter,
    fuelTypeFilter,
    drivetrainFilter,
    wheelSizeFilter,
    gearboxFilter,
    powerFilter,
  ]);

  const fullListOfFilters = useMemo(() => {
    //   return [...staticFilterOrder, ...configuredFilterOrder];
    // }, [configuredFilterOrder, staticFilterOrder]);
    return [...configuredFilterOrder];
  }, [configuredFilterOrder]);

  const listOfFiltersToRender = useMemo(() => {
    if (isTier3page && isUsedVehicleFilter)
      return fullListOfFilters
        .filter((vehicleFilter) => usedFiltersListTier3.includes(vehicleFilter.value))
        .filter((vehicleFilter) => filterGroupsToRender.includes(vehicleFilter.value));
    if (isTier3page && !isUsedVehicleFilter) {
      return fullListOfFilters
        .filter((vehicleFilter) => NEW_VEHICLE_FILTERS_LIST_TIER_3.includes(vehicleFilter.value))
        .filter((vehicleFilter) => filterGroupsToRender.includes(vehicleFilter.value));
    }

    if (isUsedVehicleFilter)
      return fullListOfFilters.filter((vehicleFilter) =>
        USED_VEHICLE_FILTERS_LIST.includes(vehicleFilter.value),
      );
    return fullListOfFilters.filter((vehicleFilter) =>
      NEW_VEHICLE_FILTERS_LIST.includes(vehicleFilter.value),
    );
  }, [
    filterGroupsToRender,
    fullListOfFilters,
    isTier3page,
    isUsedVehicleFilter,
    usedFiltersListTier3,
  ]);

  return listOfFiltersToRender;
};

export default useFilterListBuilder;
